// Google Analytics 4 helper.
// Lightweight wrapper around gtag.js. Safe to call even if GA isn't initialized —
// every function becomes a no-op. Configured dynamically with the GA4 Measurement
// ID stored in /api/settings so admins can change it without a redeploy.

let currentMeasurementId = null;
let scriptInjected = false;

function ensureDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

/**
 * Load the gtag.js script and configure GA4 with the given Measurement ID.
 * Called once at app startup after settings are fetched. Idempotent.
 */
export function initGA(measurementId) {
  if (!measurementId || typeof window === "undefined") return;
  if (currentMeasurementId === measurementId && scriptInjected) return;

  const gtag = ensureDataLayer();
  if (!gtag) return;

  // Inject the script only once
  if (!scriptInjected) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
    scriptInjected = true;
    gtag("js", new Date());
  }

  // Disable automatic page_view — SPA sends them on route change
  gtag("config", measurementId, { send_page_view: false });
  currentMeasurementId = measurementId;
}

export function isGAEnabled() {
  return !!currentMeasurementId;
}

export function trackPageView(path, title) {
  if (!isGAEnabled()) return;
  const gtag = ensureDataLayer();
  if (!gtag) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    send_to: currentMeasurementId,
  });
}

// ---------- E-commerce helpers ----------

function toGAItem(product, quantity = 1) {
  if (!product) return null;
  return {
    item_id: product.id,
    item_name: product.name,
    price: Number(product.price) || 0,
    item_category: product.category,
    quantity,
  };
}

export function trackViewItem(product) {
  if (!isGAEnabled() || !product) return;
  const gtag = ensureDataLayer();
  gtag("event", "view_item", {
    currency: "EUR",
    value: Number(product.price) || 0,
    items: [toGAItem(product)].filter(Boolean),
  });
}

export function trackAddToCart(product, quantity = 1) {
  if (!isGAEnabled() || !product) return;
  const gtag = ensureDataLayer();
  gtag("event", "add_to_cart", {
    currency: "EUR",
    value: (Number(product.price) || 0) * quantity,
    items: [toGAItem(product, quantity)].filter(Boolean),
  });
}

export function trackBeginCheckout(cartItems, total) {
  if (!isGAEnabled()) return;
  const gtag = ensureDataLayer();
  const items = (cartItems || []).map((i) => ({
    item_id: i.product_id,
    item_name: i.name,
    price: Number(i.price) || 0,
    quantity: i.quantity,
  }));
  gtag("event", "begin_checkout", {
    currency: "EUR",
    value: Number(total) || 0,
    items,
  });
}

export function trackPurchase({ transactionId, total, items }) {
  if (!isGAEnabled()) return;
  const gtag = ensureDataLayer();
  const gaItems = (items || []).map((i) => ({
    item_id: i.product_id,
    item_name: i.name,
    price: Number(i.price) || 0,
    quantity: i.quantity,
  }));
  gtag("event", "purchase", {
    transaction_id: transactionId,
    currency: "EUR",
    value: Number(total) || 0,
    items: gaItems,
  });
}
